import React, { useMemo } from "react";
import { Layer, Source } from "react-map-gl";
import { IData, IDevice } from "../../types/deviceInterface";
import { IField } from "../../types/fieldInterface";

type SurfaceColorFunction = (value: number | undefined, params: Record<string, number>) => string;

interface SurfaceSample {
  longitude: number;
  latitude: number;
  value: number;
}

interface Props {
  field: Pick<IField, "id" | "area">;
  devices: IDevice[];
  plotkey: keyof IData;
  colorFunction: SurfaceColorFunction;
  colorParams: Record<string, number>;
  sourceId?: string;
  layerId?: string;
  opacity?: number;
}

function getDeviceMetricValue(device: IDevice, plotkey: keyof IData): number | undefined {
  const rawValue = device.data?.[plotkey];
  const numericValue = rawValue === undefined || rawValue === null ? NaN : Number(rawValue);

  if (!Number.isFinite(numericValue)) {
    return undefined;
  }

  if (numericValue === 0 && !String(plotkey).startsWith("moisture_index")) {
    return undefined;
  }

  return numericValue;
}

function pointInRing(point: [number, number], ring: number[][]): boolean {
  let isInside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];

    const intersects =
      yi > point[1] !== yj > point[1] &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi || Number.EPSILON) + xi;

    if (intersects) {
      isInside = !isInside;
    }
  }

  return isInside;
}

function pointInPolygon(point: [number, number], polygon: number[][][]): boolean {
  if (polygon.length === 0 || !pointInRing(point, polygon[0])) {
    return false;
  }

  for (let index = 1; index < polygon.length; index += 1) {
    if (pointInRing(point, polygon[index])) {
      return false;
    }
  }

  return true;
}

function estimateValueAtPoint(
  point: [number, number],
  samples: SurfaceSample[],
): number | undefined {
  if (samples.length === 0) {
    return undefined;
  }

  let weightedValue = 0;
  let weightSum = 0;

  for (const sample of samples) {
    const dx = point[0] - sample.longitude;
    const dy = point[1] - sample.latitude;
    const distanceSquared = dx * dx + dy * dy;

    if (distanceSquared < 1e-12) {
      return sample.value;
    }

    const weight = 1 / distanceSquared;
    weightedValue += sample.value * weight;
    weightSum += weight;
  }

  if (weightSum === 0) {
    return undefined;
  }

  return weightedValue / weightSum;
}

const RenderInterpolatedSurface: React.FC<Props> = ({
  field,
  devices,
  plotkey,
  colorFunction,
  colorParams,
  sourceId = "interpolated-surface-source",
  layerId = "interpolated-surface-layer",
  opacity = 0.45,
}) => {
  const surfaceData = useMemo(() => {
    if (!field.area || !Array.isArray(devices) || devices.length === 0) {
      return null;
    }

    const samples = devices
      .map((device) => {
        const numericValue = getDeviceMetricValue(device, plotkey);

        if (
          numericValue === undefined ||
          !Number.isFinite(device.longitude) ||
          !Number.isFinite(device.latitude) ||
          typeof device.longitude !== "number" ||
          typeof device.latitude !== "number"
        ) {
          return null;
        }

        return {
          longitude: device.longitude,
          latitude: device.latitude,
          value: numericValue,
        };
      })
      .filter((sample): sample is SurfaceSample => sample !== null);

    if (samples.length < 2) {
      return null;
    }

    const gridResolution = 18;
    const features = Object.values(field.area).flatMap((area: any) => {
      const polygon = area.geometry.coordinates;

      if (!polygon?.length || !polygon[0]?.length) {
        return [];
      }

      const outerRing = polygon[0];
      const longitudes = outerRing.map(([longitude]: number[]) => longitude);
      const latitudes = outerRing.map(([, latitude]: number[]) => latitude);

      if (
        longitudes.length === 0 ||
        latitudes.length === 0 ||
        longitudes.some((value: number) => !Number.isFinite(value)) ||
        latitudes.some((value: number) => !Number.isFinite(value))
      ) {
        return [];
      }

      const minLongitude = Math.min(...longitudes);
      const maxLongitude = Math.max(...longitudes);
      const minLatitude = Math.min(...latitudes);
      const maxLatitude = Math.max(...latitudes);

      if (minLongitude === maxLongitude || minLatitude === maxLatitude) {
        return [];
      }

      const longitudeStep = (maxLongitude - minLongitude) / gridResolution;
      const latitudeStep = (maxLatitude - minLatitude) / gridResolution;

      if (longitudeStep === 0 || latitudeStep === 0) {
        return [];
      }

      const cellFeatures = [];

      for (let xIndex = 0; xIndex < gridResolution; xIndex += 1) {
        for (let yIndex = 0; yIndex < gridResolution; yIndex += 1) {
          const west = minLongitude + xIndex * longitudeStep;
          const east = west + longitudeStep;
          const south = minLatitude + yIndex * latitudeStep;
          const north = south + latitudeStep;
          const center: [number, number] = [(west + east) / 2, (south + north) / 2];

          if (!pointInPolygon(center, polygon)) {
            continue;
          }

          const estimatedValue = estimateValueAtPoint(center, samples);

          if (estimatedValue === undefined) {
            continue;
          }

          cellFeatures.push({
            type: "Feature",
            properties: {
              value: estimatedValue,
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [west, south],
                  [east, south],
                  [east, north],
                  [west, north],
                  [west, south],
                ],
              ],
            },
          });
        }
      }

      return cellFeatures;
    });

    if (features.length === 0) {
      return null;
    }

    return {
      type: "FeatureCollection",
      features,
      metadata: {
        minValue: Math.min(...samples.map((sample) => sample.value)),
        maxValue: Math.max(...samples.map((sample) => sample.value)),
      },
    };
  }, [devices, field.area, plotkey]);

  if (!surfaceData) {
    return null;
  }

  const metadata = (surfaceData as any).metadata;
  const derivedMin = typeof metadata?.minValue === "number" ? metadata.minValue : 0;
  const derivedMax = typeof metadata?.maxValue === "number" ? metadata.maxValue : 1;
  const configuredMin = typeof colorParams.min === "number" ? colorParams.min : derivedMin;
  const configuredMax = typeof colorParams.max === "number" ? colorParams.max : derivedMax;
  const span = configuredMax - configuredMin;
  const stopMin = Number.isFinite(span) && span !== 0 ? configuredMin : configuredMin - 0.5;
  const stopMax = Number.isFinite(span) && span !== 0 ? configuredMax : configuredMax + 0.5;

  if (!Number.isFinite(stopMin) || !Number.isFinite(stopMax)) {
    return null;
  }

  const valueStops = [
    stopMin,
    stopMin + (stopMax - stopMin) * 0.25,
    stopMin + (stopMax - stopMin) * 0.5,
    stopMin + (stopMax - stopMin) * 0.75,
    stopMax,
  ];

  return (
    <Source id={sourceId} type="geojson" data={surfaceData as any}>
      <Layer
        id={layerId}
        type="fill"
        paint={{
          "fill-color": [
            "interpolate",
            ["linear"],
            ["get", "value"],
            valueStops[0],
            colorFunction(valueStops[0], colorParams),
            valueStops[1],
            colorFunction(valueStops[1], colorParams),
            valueStops[2],
            colorFunction(valueStops[2], colorParams),
            valueStops[3],
            colorFunction(valueStops[3], colorParams),
            valueStops[4],
            colorFunction(valueStops[4], colorParams),
          ],
          "fill-opacity": opacity,
        }}
      />
    </Source>
  );
};

export default RenderInterpolatedSurface;
